import * as RadixScrollArea from '@radix-ui/react-scroll-area';
import clsx from 'clsx';
import {
  ComponentPropsWithoutRef,
  createContext,
  forwardRef,
  MutableRefObject,
  RefObject,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { vars } from 'src/core/themes';
import { styledComponent } from 'src/core/utils';
import {
  BackIcon,
  ExpandIcon,
  FoldIcon,
  ForwardIcon,
  IconsFill,
} from 'src/svgs/Viagogo';

import * as styles from './ScrollArea.css';
import { AnimateScrollDirection, useAnimateScroll } from './useAnimateScroll';
import { useScrollEdges } from './useScrollEdges';

const ScrollAreaContext = createContext<{
  viewportRef: RefObject<HTMLDivElement>;
}>({
  viewportRef: {
    current: null,
  },
});

const Root = styledComponent(RadixScrollArea.Root, styles.root);

const Viewport = styledComponent(RadixScrollArea.Viewport, styles.viewport);

const Scrollbar = styledComponent(RadixScrollArea.Scrollbar, styles.scrollbar);

const Thumb = styledComponent(RadixScrollArea.Thumb, styles.thumb);

const Corner = RadixScrollArea.Corner;

export interface ScrollAreaActions {
  startScroll: (direction: AnimateScrollDirection) => void;
}

export interface ScrollAreaRefActions {
  scrollAreaRefActions?: MutableRefObject<ScrollAreaActions | null>;
  scrollButtonClassName?: Record<'up' | 'down' | 'left' | 'right', string>;
  showScrollButton?: boolean;
}

/**
 * Use for a scrolling area that overlays the scrollbar on top of the content instead of offsetting
 * the content to the side.
 */
export const ScrollArea = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof Root> & {
    /**
     * Whether to show arrows at the edge of the scroll container that can be hovered to scroll.
     */
    showScrollEdges?: boolean;
  } & ScrollAreaRefActions
>(function ScrollArea(
  {
    children,
    showScrollEdges = false,
    showScrollButton = true,
    scrollAreaRefActions,
    scrollButtonClassName,
    ...props
  },
  ref
) {
  const viewportRef = useRef<HTMLDivElement>(null);
  return (
    <ScrollAreaContext.Provider
      value={{
        viewportRef,
      }}
    >
      <Root ref={ref} {...props}>
        <Viewport ref={viewportRef}>{children}</Viewport>
        {showScrollEdges && (
          <ScrollEdges
            scrollAreaRefActions={scrollAreaRefActions}
            scrollButtonClassName={scrollButtonClassName}
            showScrollButton={showScrollButton}
          />
        )}
        <Scrollbar id="scrollbar" orientation="vertical">
          <Thumb />
        </Scrollbar>
        <Scrollbar id="scrollbar" orientation="horizontal">
          <Thumb />
        </Scrollbar>
        <Corner />
      </Root>
    </ScrollAreaContext.Provider>
  );
});

type ScrollEdgesProps = ScrollAreaRefActions;

function ScrollEdges({
  scrollAreaRefActions,
  scrollButtonClassName,
  showScrollButton = true,
}: ScrollEdgesProps) {
  const { viewportRef } = useContext(ScrollAreaContext);
  const { scrollDirection, startScroll, endScroll } =
    useAnimateScroll(viewportRef);
  const edges = useScrollEdges(viewportRef);

  useEffect(() => {
    if (
      (scrollDirection.current === 'up' && edges.atTop) ||
      (scrollDirection.current === 'down' && edges.atBottom) ||
      (scrollDirection.current === 'left' && edges.atLeft) ||
      (scrollDirection.current === 'right' && edges.atRight)
    ) {
      endScroll();
    }
  }, [edges, scrollDirection, endScroll]);

  useImperativeHandle(
    scrollAreaRefActions,
    () => ({
      startScroll: startScroll,
    }),
    [startScroll]
  );

  return (
    <>
      {!edges.atTop && (
        <div
          className={clsx(
            styles.arrowContainer['up'],
            scrollButtonClassName?.['up']
          )}
          onMouseEnter={() => startScroll('up')}
          onMouseLeave={() => endScroll()}
        >
          {showScrollButton && (
            <FoldIcon size={vars.iconSize.s} fill={IconsFill.textBrand} />
          )}
        </div>
      )}
      {!edges.atBottom && (
        <div
          className={clsx(
            styles.arrowContainer['down'],
            scrollButtonClassName?.['down']
          )}
          onMouseEnter={() => startScroll('down')}
          onMouseLeave={() => endScroll()}
        >
          {showScrollButton && (
            <ExpandIcon size={vars.iconSize.s} fill={IconsFill.textBrand} />
          )}
        </div>
      )}
      {!edges.atLeft && (
        <div
          className={clsx(
            styles.arrowContainer['left'],
            scrollButtonClassName?.['left']
          )}
          onMouseEnter={() => startScroll('left')}
          onMouseLeave={() => endScroll()}
        >
          {showScrollButton && (
            <BackIcon size={vars.iconSize.s} fill={IconsFill.textBrand} />
          )}
        </div>
      )}
      {!edges.atRight && (
        <div
          className={clsx(
            styles.arrowContainer['right'],
            scrollButtonClassName?.['right']
          )}
          onMouseEnter={() => startScroll('right')}
          onMouseLeave={() => endScroll()}
        >
          {showScrollButton && (
            <ForwardIcon size={vars.iconSize.s} fill={IconsFill.textBrand} />
          )}
        </div>
      )}
    </>
  );
}
